import { useApolloClient } from '@apollo/client'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { ChartTimeframeProviderContext } from '@/components/shared/analytics/chart-timeframe/context/context'
import { LogFilterProviderContext } from '@/components/shared/log-filters/context/context'
import { filtersToQuery } from '@/components/shared/log-filters/utils/filters-to-query'
import { LogsKeyboardShortcuts } from '@/components/shared/logs-explorer/components/logs-keyboard-shortcuts'
import { LogsExplorerContext } from '@/components/shared/logs-explorer/context/context'
import { useLogStream } from '@/components/shared/logs-explorer/hooks/use-log-stream'
import { logsToText } from '@/components/shared/logs-explorer/utils/logs-to-text'
import { ToasterContent } from '@/components/sv/toaster-content'
import { getTimeframes } from '@/config/charts'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useIdProject } from '@/lib/hooks'
import type { LogAttributeFilter } from '@/components/shared/log-filters/types'
import type {
  DownloadLogsMutation,
  DownloadLogsMutationVariables,
} from '@/components/shared/logs-explorer/context/provider.types.gen'
import type {
  LogsExplorerProviderProps,
  LogsExplorerState,
  ResolvedLogFilter,
  SeverityLevel,
  TimeframeState,
} from '@/components/shared/logs-explorer/types'
import type { DownloadFormat, Log } from '@/graphql.types'

const DOWNLOAD_LOGS = gql`
  mutation DownloadLogs(
    $idCompany: String!
    $idProject: String
    $idService: String!
    $serviceType: LogService!
    $timeframe: MetricsTimeFrameInput!
    $filters: [MetricsFilterInput!]
    $format: DownloadFormat!
    $limit: Int
  ) {
    downloadLogs(
      idCompany: $idCompany
      idProject: $idProject
      idService: $idService
      serviceType: $serviceType
      timeframe: $timeframe
      filters: $filters
      format: $format
      limit: $limit
    ) {
      content
      format
      rowCount
    }
  }
`

const deepParseJson = (value: unknown): unknown => {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        return deepParseJson(JSON.parse(trimmed))
      } catch {
        return value
      }
    }
    return value
  }

  if (Array.isArray(value)) {
    return value.map(deepParseJson)
  }

  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        k,
        deepParseJson(v),
      ])
    )
  }

  return value
}

const collectKeys = (obj: unknown, prefix: string, keys: Set<string>): void => {
  const parsed = deepParseJson(obj)
  if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      const path = prefix ? `${prefix}.${k}` : k
      keys.add(path)
      if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
        collectKeys(v, path, keys)
      }
    }
  }
}

type UrlState = {
  search?: string
  timeframeKey?: string
  startDate?: string
  endDate?: string
  severity?: SeverityLevel
  tags?: Record<string, string>
  filters?: Array<{ key: string; operator: string; value?: string }>
}

const LOGS_FILTER_PARAM = 'logsFilter'

const encodeUrlState = (state: UrlState): string => {
  try {
    const json = JSON.stringify(state)
    return btoa(json)
  } catch {
    return ''
  }
}

const decodeUrlState = (encoded: string): UrlState | null => {
  try {
    const json = atob(encoded)
    return JSON.parse(json) as UrlState
  } catch {
    return null
  }
}

export const LogsExplorerProvider = ({
  config,
  children,
}: LogsExplorerProviderProps) => {
  const { idCompany } = useIdCompany()
  const { idProject } = useIdProject()
  const client = useApolloClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const getInitialState = useCallback(() => {
    const encoded = searchParams.get(LOGS_FILTER_PARAM)
    if (!encoded) {
      return null
    }
    return decodeUrlState(encoded)
  }, [])

  const initialState = useMemo(() => getInitialState(), [])

  const [searchText, setSearchText] = useState(initialState?.search ?? '')
  const displaySearchText = searchText
  const setDisplaySearchText = setSearchText

  // Timeframe state
  const [timeframe, setTimeframe] = useState<TimeframeState>(() => {
    const timeframes = getTimeframes()
    if (initialState?.timeframeKey) {
      const preset = timeframes.find((t) => t.key === initialState.timeframeKey)
      if (preset) {
        return preset
      }
    }
    if (initialState?.startDate && initialState?.endDate) {
      return {
        startDate: new Date(initialState.startDate),
        endDate: new Date(initialState.endDate),
        intervalInSeconds: 60,
      }
    }
    return timeframes[0]
  })

  // Tags state (mutable overrides, merged with config.tags)
  const [tags, setTags] = useState<Record<string, string | undefined>>(() => ({
    ...config.tags,
    ...initialState?.tags,
  }))

  // Attribute filter state
  const [attributeFilters, setAttributeFilters] = useState<
    LogAttributeFilter[]
  >(() => (initialState?.filters as LogAttributeFilter[]) ?? [])
  const [isFilterSidebarCollapsed, setIsFilterSidebarCollapsed] =
    useState(false)

  // Streaming state
  const [isStreaming, setIsStreaming] = useState(true)

  // Severity filter
  const [severity, setSeverity] = useState<SeverityLevel | null>(
    initialState?.severity ?? null
  )

  // Key suggestions
  const [keySuggestions, setKeySuggestions] = useState<string[]>([])
  const knownKeysRef = useRef(new Set<string>())

  // Download state
  const [isDownloading, setIsDownloading] = useState(false)

  // Refresh key for forcing chart remount
  const [refreshKey, setRefreshKey] = useState(0)

  // Chart visibility
  const [isChartCollapsed, setIsChartCollapsed] = useState(false)

  // Sync state to URL
  const isInitialMount = useRef(true)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    const urlState: UrlState = {}

    if (searchText) {
      urlState.search = searchText
    }

    if (timeframe.key) {
      urlState.timeframeKey = timeframe.key
    } else if (timeframe.startDate && timeframe.endDate) {
      urlState.startDate = timeframe.startDate.toISOString()
      urlState.endDate = timeframe.endDate.toISOString()
    }

    if (severity) {
      urlState.severity = severity
    }

    const tagsToStore = Object.entries(tags).reduce<Record<string, string>>(
      (acc, [key, value]) => {
        if (value && !config.tags?.[key]) {
          acc[key] = value
        }
        return acc
      },
      {}
    )
    if (Object.keys(tagsToStore).length > 0) {
      urlState.tags = tagsToStore
    }

    if (attributeFilters.length > 0) {
      urlState.filters = attributeFilters.map((f) => ({
        key: f.key,
        operator: f.operator,
        value: f.value,
      }))
    }

    const hasState = Object.keys(urlState).length > 0
    const encoded = hasState ? encodeUrlState(urlState) : null

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (encoded) {
          next.set(LOGS_FILTER_PARAM, encoded)
        } else {
          next.delete(LOGS_FILTER_PARAM)
        }
        return next
      },
      { replace: true }
    )
  }, [
    searchText,
    timeframe,
    severity,
    tags,
    attributeFilters,
    config.tags,
    setSearchParams,
  ])

  // Scroll-to-bottom registration
  const scrollToBottomRef = useRef<() => void>(() => {})

  const registerScrollToBottom = useCallback((fn: () => void) => {
    scrollToBottomRef.current = fn
  }, [])

  const scrollToBottom = useCallback(() => {
    scrollToBottomRef.current()
  }, [])

  // Run query registration (for keyboard shortcuts)
  const runQueryRef = useRef<() => void>(() => {})

  const registerRunQuery = useCallback((fn: () => void) => {
    runQueryRef.current = fn
  }, [])

  const runQuery = useCallback(() => {
    runQueryRef.current()
  }, [])

  // Keyboard shortcuts dialog
  const [isShortcutsDialogOpen, setIsShortcutsDialogOpen] = useState(false)

  // Compute resolved filter from all sources
  const isCustomDateRange = !timeframe.key
  const metricsFilters = useMemo(() => {
    const filters = filtersToQuery(attributeFilters)

    if (severity) {
      filters.push({
        key: 'severity',
        operator: 'EQUALS',
        value: severity,
      })
    }

    return filters
  }, [attributeFilters, severity])

  const resolvedFilter = useMemo<ResolvedLogFilter>(
    () => ({
      service: config.service,
      idService: config.idService,
      tags: { ...config.tags, ...tags },
      from: config.from ?? timeframe.startDate,
      to: config.to ?? (isCustomDateRange ? timeframe.endDate : undefined),
      filterText: searchText || undefined,
      filters: metricsFilters.length > 0 ? metricsFilters : undefined,
    }),
    [
      config.service,
      config.idService,
      config.tags,
      tags,
      config.from,
      config.to,
      timeframe.startDate,
      timeframe.endDate,
      isCustomDateRange,
      searchText,
      metricsFilters,
    ]
  )

  // Stream hook
  const {
    logs,
    isLoading,
    isRefreshing,
    refresh: refreshLogs,
    fetchLogs,
    setLogs,
  } = useLogStream({
    filter: resolvedFilter,
    type: config.type,
    isAutoStopEnabled: config.isAutoStopEnabled,
    isStreaming,
    setIsStreaming,
  })

  const refresh = useCallback(() => {
    refreshLogs()
    setRefreshKey((k) => k + 1)
  }, [refreshLogs])

  // Derive key suggestions from logs
  useEffect(() => {
    const keys = knownKeysRef.current
    const prevSize = keys.size

    keys.add('id')
    keys.add('severity')
    keys.add('service')
    keys.add('message')
    keys.add('timestamp')

    for (const log of logs) {
      collectKeys(log.attributes, 'attributes', keys)
    }

    if (keys.size !== prevSize) {
      setKeySuggestions(Array.from(keys).sort())
    }
  }, [logs])

  // Download handler
  const downloadLogs = useCallback(
    async (format: DownloadFormat) => {
      setIsDownloading(true)
      try {
        const { data } = await client.mutate<
          DownloadLogsMutation,
          DownloadLogsMutationVariables
        >({
          mutation: DOWNLOAD_LOGS,
          variables: {
            idCompany,
            idProject,
            idService: config.idService ?? '',
            serviceType: config.service!,
            timeframe: {
              start: (resolvedFilter.from ?? new Date()).toISOString(),
              end: (resolvedFilter.to ?? new Date()).toISOString(),
            },
            filters: resolvedFilter.filters?.length
              ? resolvedFilter.filters
              : undefined,
            format,
          },
        })

        const content = data?.downloadLogs.content ?? ''
        const mimeType = format === 'JSON' ? 'application/json' : 'text/csv'
        const extension = format === 'JSON' ? 'json' : 'csv'
        const blob = new Blob([content], { type: mimeType })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `logs-${new Date().toISOString()}.${extension}`
        a.click()
        URL.revokeObjectURL(url)
      } finally {
        setIsDownloading(false)
      }
    },
    [
      client,
      idCompany,
      idProject,
      config.idService,
      config.service,
      resolvedFilter,
    ]
  )

  // Copy handler
  const [isCopying, setIsCopying] = useState(false)

  const copyLogs = useCallback(async () => {
    setIsCopying(true)
    try {
      await navigator.clipboard.writeText(logsToText(logs))
      toast(
        <ToasterContent variant="success">
          Logs copied to clipboard
        </ToasterContent>
      )
    } catch {
      toast(
        <ToasterContent variant="error">Failed to copy logs</ToasterContent>
      )
    } finally {
      setIsCopying(false)
    }
  }, [logs])

  // Load more: fetch logs before a given timestamp
  const fetchLogsBefore = useCallback(
    async (to: Date) => {
      return fetchLogs({ to: to as any })
    },
    [fetchLogs]
  )

  const prependLogs = useCallback(
    (newLogs: Log[]) => {
      setLogs((prev) => {
        const existingIds = new Set(prev.map((l) => l.id))
        const unique = newLogs.filter((l) => !existingIds.has(l.id))
        return [...unique, ...prev]
      })
    },
    [setLogs]
  )

  const state: LogsExplorerState = {
    config,
    logs,
    isLoading,
    isStreaming,
    setIsStreaming,
    refresh,
    isRefreshing,
    searchText,
    displaySearchText,
    setDisplaySearchText,
    timeframe,
    setTimeframe,
    tags,
    setTags,
    severity,
    setSeverity,
    attributeFilters,
    setAttributeFilters,
    isFilterSidebarCollapsed,
    setIsFilterSidebarCollapsed,
    keySuggestions,
    downloadLogs,
    isDownloading,
    copyLogs,
    isCopying,
    scrollToBottom,
    registerScrollToBottom,
    fetchLogsBefore,
    prependLogs,
    resolvedFilter,
    isShortcutsDialogOpen,
    setIsShortcutsDialogOpen,
    registerRunQuery,
    runQuery,
    refreshKey,
    isChartCollapsed,
    setIsChartCollapsed,
  }

  // Provide ChartTimeframe and LogFilter contexts so existing components
  // (ChartTimeframe selector, LogFilterSidebar, LogVolumeChart) work unchanged.
  return (
    <LogsExplorerContext.Provider value={state}>
      <ChartTimeframeProviderContext.Provider
        value={{
          timeframe,
          setTimeframe,
          isLive: false,
          isRefreshing: false,
          refresh: () => null,
        }}
      >
        <LogFilterProviderContext.Provider
          value={{
            filters: attributeFilters,
            setFilters: setAttributeFilters,
            isCollapsed: isFilterSidebarCollapsed,
            setIsCollapsed: setIsFilterSidebarCollapsed,
            registerRunQuery,
          }}
        >
          {children}
          <LogsKeyboardShortcuts />
        </LogFilterProviderContext.Provider>
      </ChartTimeframeProviderContext.Provider>
    </LogsExplorerContext.Provider>
  )
}
